import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { Header } from "./header";
import { useIsWide } from "./hooks";
import { activeNavId, buildNav } from "./navModel";
import { Drawer, Rail, Sidebar } from "./sidebar";
import { readGroups, readSidebarOpen, writeGroups, writeSidebarOpen } from "./shellStorage";
import styles from "./appShell.module.css";

/**
 * The one shell every logged-in page renders inside (GROWTH-8166): the sidebar (or its 64px rail, or the drawer under 1320px) and
 * the header. The page itself is `children`, in the header's 1480px column (GROWTH-8171) so the two share a left edge: a `div`, not
 * `main`, because the listing editor renders its own `main`.
 */
const AppShell = ({ keycloak, children }) => {
  const { pathname } = useLocation();
  const inboxCount = Math.max(0, Number(useSelector(state => state.account.profile?.new_messages)) || 0);
  const nav = useMemo(() => buildNav(), []);
  const activeId = activeNavId(pathname);
  const wide = useIsWide();
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarOpen);
  const [openMap, setOpenMap] = useState(readGroups);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The sidebar is in the page again once the viewport grows past the drawer widths.
  useEffect(() => {
    if (wide) {
      setDrawerOpen(false);
    }
  }, [wide]);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const setSidebar = useCallback(open => {
    writeSidebarOpen(open);
    setSidebarOpen(open);
  }, []);

  const toggleGroup = useCallback(
    (key, open) =>
      setOpenMap(current => {
        const next = { ...current, [key]: open };

        writeGroups(next);

        return next;
      }),
    []
  );

  const sidebarVisible = wide && sidebarOpen;
  const accountItems = nav[nav.length - 1].items;

  return (
    <div className={styles.page}>
      {sidebarVisible && (
        <Sidebar
          nav={nav}
          activeId={activeId}
          openMap={openMap}
          onToggle={toggleGroup}
          onHide={() => setSidebar(false)}
          inboxCount={inboxCount}
        />
      )}
      {wide && !sidebarOpen && <Rail nav={nav} activeId={activeId} onShow={() => setSidebar(true)} inboxCount={inboxCount} />}
      {!wide && <Drawer nav={nav} activeId={activeId} open={drawerOpen} onClose={closeDrawer} inboxCount={inboxCount} />}
      <div className={styles.column}>
        <Header
          keycloak={keycloak}
          showMenu={!sidebarVisible}
          onOpenMenu={() => (wide ? setSidebar(true) : setDrawerOpen(true))}
          drawerOpen={drawerOpen}
          accountItems={accountItems}
          activeId={activeId}
        />
        <div className={styles.main}>{children}</div>
      </div>
    </div>
  );
};

export default AppShell;
